import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section } from "./Section";
import { LucideIcon } from "@/components/marketing/LucideIcon";
import { HOW_IT_WORKS } from "@/content/landing";

export function HowItWorks() {
  const reduce = useReducedMotion();
  return (
    <Section
      id="how-it-works"
      testId="landing-how-it-works"
      eyebrow={HOW_IT_WORKS.eyebrow}
      title={HOW_IT_WORKS.title}
      subtitle={HOW_IT_WORKS.subtitle}
    >
      <div className="relative grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* connecting line */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-sema-mid/40 to-transparent md:block"
        />
        {HOW_IT_WORKS.steps.map((s, i) => (
          <motion.div
            key={s.n}
            data-testid={`how-step-${s.n}`}
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, ease: "easeOut", delay: i * 0.12 }}
            className="relative rounded-2xl border border-border/60 bg-card/50 p-7 backdrop-blur"
          >
            <div className="flex items-center justify-between">
              <span
                className="inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg shadow-sema-primary/30 sema-cta-gradient"
              >
                <LucideIcon name={s.icon} className="h-6 w-6" />
              </span>
              <span className="font-mono text-4xl font-bold text-foreground/10">{s.n}</span>
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-foreground">{s.title}</h3>
            <p className="mt-2.5 font-body text-sm leading-relaxed text-muted-foreground">{s.body}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to="/signup">
          <Button
            data-testid="how-it-works-cta-primary"
            size="lg"
            className="group h-12 rounded-full sema-cta-gradient px-6 font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/30 transition-transform hover:-translate-y-0.5"
          >
            Start free sandbox
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </Link>
        <Link to="/contact">
          <Button
            data-testid="how-it-works-cta-secondary"
            variant="outline"
            size="lg"
            className="h-12 rounded-full border-border/80 px-6 font-mono text-xs uppercase tracking-[0.22em] text-foreground hover:bg-muted"
          >
            Talk to sales
          </Button>
        </Link>
      </div>
    </Section>
  );
}
